// Cast view: every persona's card (with their live sandbox screen when they
// are using aa-app), the shared thought stream and the wake schedule.
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { api } from '../../convex/_generated/api';
import type { AgentEventKind } from '../../convex/shared/validators';
import type { LiveSessionView } from '../../convex/shared/views';
import { FeedList } from '../components/Feed';
import { PersonaCardView } from '../components/persona';
import { fmtClock, fmtRelative, humanize, shortName } from '../lib/format';
import { useCast, useNow } from '../lib/hooks';
import { useSimQuery } from '../lib/session';
import { Avatar } from '../ui/Avatar';
import { Empty, Panel, Segmented, Spinner } from '../ui/primitives';

type FeedFilter = 'all' | 'minds' | 'gates' | 'actions';
const FILTER_KINDS: Record<FeedFilter, AgentEventKind[] | null> = {
  all: null,
  minds: ['thought', 'decision', 'speech'],
  gates: ['gate', 'wake', 'sleep'],
  actions: ['action', 'observation', 'error', 'budget'],
};

export function CastView() {
  const now = useNow(5_000);
  const cast = useCast();
  const sessions = useSimQuery(api.browser.sessions.listLiveSessions, {});
  const feed = useSimQuery(api.agents.feed.listFeed, { limit: 80 });
  const wakes = useSimQuery(api.agents.loop.listUpcomingWakes, { limit: 24 });
  const [filter, setFilter] = useState<FeedFilter>('all');

  const sessionByPersona = useMemo(() => {
    const m = new Map<string, LiveSessionView>();
    for (const s of sessions ?? []) if (s.personaId && s.purpose === 'app') m.set(s.personaId, s);
    return m;
  }, [sessions]);
  const kinds = FILTER_KINDS[filter];
  const events = feed?.filter((e) => !kinds || kinds.includes(e.kind));

  return (
    <div className="page">
      <header className="page-head">
        <h1>The cast</h1>
        <p>Eight people at Volt &amp; Vine Electric, each with their own goals, secrets and bad habits. Click anyone to see their screen, thoughts and inbox.</p>
      </header>
      {cast === undefined ? <Spinner /> : null}
      {cast?.cards.length === 0 ? <Empty icon="people" title="Nobody has clocked in">Seed the scenario to meet the cast.</Empty> : null}
      <div className="cast-grid">
        {cast?.cards.map((c) => <PersonaCardView key={c._id} card={c} session={sessionByPersona.get(c._id)} now={now} variant="grid" />)}
      </div>
      <div className="two-col">
        <Panel
          title="Thought stream"
          icon="brain"
          actions={
            <Segmented<FeedFilter>
              ariaLabel="Filter the stream"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'minds', label: 'Minds' },
                { value: 'gates', label: 'Gates' },
                { value: 'actions', label: 'Actions' },
              ]}
            />
          }
        >
          <FeedList events={events} cast={cast} now={now} />
        </Panel>
        <Panel title="Coming up" icon="clock">
          {wakes === undefined ? <Spinner /> : null}
          {wakes?.length === 0 ? <Empty icon="zzz" title="No plans yet">Wakes appear here once the agents subsystem schedules them.</Empty> : null}
          <ol className="wake-list">
            {wakes?.map((w) => {
              const card = cast?.byId.get(w.personaId);
              return (
                <li key={w._id} className={`wake status-${w.status}`}>
                  <time>{fmtClock(w.at)}</time>
                  {card ? (
                    <Link href={`/agent/${card.key}`} className="wake-who">
                      <Avatar personaKey={card.key} mood={card.mood} size={22} />
                      {shortName(card.name)}
                    </Link>
                  ) : null}
                  <span className="wake-reason">{w.reason}</span>
                  <span className="wake-meta">
                    {humanize(w.source)} · {fmtRelative(w.at, now)}
                  </span>
                </li>
              );
            })}
          </ol>
        </Panel>
      </div>
    </div>
  );
}
